/**
 * Advanced Therapy RAG with Emotional Intelligence and Conversation History Integration
 * Creates world-class therapeutic intervention through multi-layered analysis
 */

import { therapeuticMethods, symptomToMethodsMap } from '../data/therapeuticMethods';
import { supabase } from '../config/supabase';

class AdvancedTherapyRAG {
  constructor() {
    this.methods = therapeuticMethods;
    this.symptomMap = symptomToMethodsMap;
    this.emotionalPatterns = this.initializeEmotionalPatterns();
    this.therapeuticTiming = this.initializeTherapeuticTiming();
    this.conversationMemory = new Map(); // Store user conversation patterns
    this.conversationCache = new Map(); // Cache recent conversation analyses
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes cache
    this.initialized = true;
  }

  initializeEmotionalPatterns() {
    return {
      // Detect emotional undertones and subtext
      'masked_depression': [
        'fine', 'okay', 'whatever', 'tired all the time', 'nothing matters',
        'going through the motions', 'empty', 'numb', 'meh', 'just existing',
        'automatic pilot', 'same old', 'pointless', 'hollow'
      ],
      'hidden_anxiety': [
        'busy', 'productive', 'perfectionist', 'control', 'planning',
        'what if', 'should', 'must', 'have to', 'need to', 'supposed to',
        'behind', 'catching up', 'overwhelmed', 'juggling'
      ],
      'suppressed_anger': [
        'frustrated', 'annoyed', 'people are', 'they always', 'unfair',
        'sick of', 'tired of', 'done with', 'fed up', 'irritated',
        'can\'t stand', 'drives me crazy', 'ridiculous'
      ],
      'attachment_wounds': [
        'always leave', 'never stay', 'alone', 'abandon', 'reject',
        'not good enough', 'too much', 'too little', 'clingy',
        'push away', 'distance', 'walls up', 'can\'t trust'
      ],
      'trauma_responses': [
        'hypervigilant', 'jumpy', 'can\'t relax', 'always watching',
        'freeze up', 'shut down', 'dissociate', 'not present',
        'triggered', 'flashback', 'panic mode', 'survival mode'
      ],
      'existential_crisis': [
        'point', 'meaning', 'purpose', 'why bother', 'what\'s the use',
        'meaningless', 'empty', 'lost', 'direction', 'drifting',
        'going nowhere', 'stuck', 'what\'s it all for'
      ],
      'shame_spiral': [
        'worthless', 'failure', 'disappointment', 'letting down',
        'not enough', 'broken', 'damaged', 'flawed', 'pathetic',
        'disgusting', 'hate myself', 'embarrassing'
      ],
      'overwhelm_patterns': [
        'too much', 'can\'t handle', 'drowning', 'suffocating',
        'crushing', 'breaking point', 'about to snap', 'overloaded',
        'can\'t breathe', 'walls closing in'
      ]
    };
  }

  initializeTherapeuticTiming() {
    return {
      'crisis_indicators': [
        'suicide', 'kill myself', 'end it', 'hurt myself', 'overdose',
        'can\'t go on', 'no point living', 'better off dead', 'want to die',
        'cutting', 'self-harm', 'plan to', 'tonight', 'pills'
      ],
      'readiness_for_change': [
        'ready', 'want to change', 'tired of', 'enough', 'different',
        'help me', 'what can I do', 'how do I', 'steps', 'action',
        'try', 'willing', 'committed', 'motivated'
      ],
      'resistance_patterns': [
        'but', 'tried everything', 'won\'t work', 'can\'t', 'impossible',
        'too hard', 'no point', 'already know', 'however', 'except',
        'doesn\'t help', 'useless', 'waste of time'
      ],
      'breakthrough_indicators': [
        'never thought', 'realize', 'see now', 'understand', 'clarity',
        'makes sense', 'aha', 'wow', 'interesting', 'eye-opening',
        'helpful', 'different perspective', 'new way'
      ],
      'rapport_building': [
        'feel safe', 'understand me', 'get it', 'heard', 'comfortable',
        'trust', 'connection', 'right', 'exactly', 'yes'
      ]
    };
  }

  async retrieveRelevantMethods(userMessage, context = {}) {
    try {
      // Quick analysis for speed
      const surfaceSymptoms = this.detectSurfaceSymptoms(userMessage);
      const hiddenEmotions = this.detectHiddenEmotions(userMessage);
      const therapeuticReadiness = this.assessTherapeuticReadiness(userMessage);
      const crisisLevel = this.assessCrisisLevel(userMessage);
      
      // Simplified conversation analysis for speed (limit to essential patterns)
      let conversationPatterns = { summary: { sessions: 0, themes: [], emotions: [], progress: 'new_user' } };
      
      // Only do deep conversation analysis if user has significant history
      if (context.userId && context.conversationHistory?.length > 5) {
        conversationPatterns = await this.analyzeConversationHistory(context.userId);
      }
      
      console.log('🔍 Fast Therapeutic Analysis:', {
        surfaceSymptoms: surfaceSymptoms.slice(0, 3), // Limit logging for speed
        hiddenEmotions: hiddenEmotions.slice(0, 2), 
        therapeuticReadiness,
        crisisLevel: crisisLevel.level
      });

      // Get methods based on streamlined analysis
      const relevantMethodIds = this.getOptimizedMethodSelection(
        surfaceSymptoms, 
        hiddenEmotions, 
        therapeuticReadiness,
        crisisLevel
      );
      
      // Retrieve and enhance method details (limit to top 2 for speed)
      const relevantMethods = relevantMethodIds.slice(0, 2).map(id => ({
        id,
        ...this.methods[id],
        therapeuticTiming: this.getTherapeuticTiming(id, therapeuticReadiness),
        emotionalDepth: this.getEmotionalDepth(id, hiddenEmotions),
        conversationFit: this.getConversationFit(id, conversationPatterns)
      }));

      // Fast ranking with essential factors only
      const rankedMethods = this.fastRanking(relevantMethods, userMessage, {
        surfaceSymptoms,
        hiddenEmotions,
        therapeuticReadiness,
        crisisLevel
      });

      return {
        methods: rankedMethods.slice(0, 2), // Always return max 2 methods
        analysis: {
          surfaceSymptoms: surfaceSymptoms.slice(0, 3),
          hiddenEmotions: hiddenEmotions.slice(0, 2),
          therapeuticReadiness,
          crisisLevel,
          conversationPatterns: conversationPatterns.summary
        }
      };

    } catch (error) {
      console.error('Error in RAG retrieval:', error);
      // Ultra-fast fallback
      return {
        methods: this.getFastFallbackMethods(userMessage),
        analysis: { error: 'Using fast fallback methods' }
      };
    }
  }

  getOptimizedMethodSelection(surfaceSymptoms, hiddenEmotions, readiness, crisis) {
    let methodIds = new Set();
    
    // Crisis intervention takes absolute priority
    if (crisis.level === 'high') {
      return ['crisis-intervention'];
    }
    
    // Fast symptom mapping (only check top symptoms)
    surfaceSymptoms.slice(0, 3).forEach(symptom => {
      const methods = this.symptomMap[symptom] || [];
      methods.slice(0, 2).forEach(methodId => methodIds.add(methodId)); // Limit methods per symptom
    });
    
    // Quick hidden emotion mapping
    hiddenEmotions.slice(0, 2).forEach(({ emotion }) => {
      const hiddenMethodMap = {
        'masked_depression': ['behavioral-activation', 'cbt-depression'],
        'hidden_anxiety': ['act-anxiety', 'cbt-anxiety'],
        'suppressed_anger': ['dbt-skills'],
        'attachment_wounds': ['schema-therapy'],
        'trauma_responses': ['emdr-trauma'],
        'existential_crisis': ['existential-therapy']
      };
      
      const methods = hiddenMethodMap[emotion] || [];
      methods.slice(0, 1).forEach(methodId => methodIds.add(methodId)); // One method per emotion max
    });
    
    // Readiness adjustment (simplified)
    if (readiness === 'resistance') {
      methodIds.add('motivational-interviewing');
    } else if (readiness === 'high_readiness') {
      methodIds.add('cbt-anxiety');
    }
    
    return Array.from(methodIds).slice(0, 3); // Max 3 methods for speed
  }

  fastRanking(methods, userMessage, analysis) {
    return methods.map(method => {
      let score = 1; // Base score
      
      // Quick relevance scoring
      analysis.surfaceSymptoms.forEach(symptom => {
        if (method.symptoms?.includes(symptom)) score += 2;
      });
      
      // Hidden emotion bonus (simplified)
      if (analysis.hiddenEmotions.length > 0) {
        score += 1;
      }
      
      // Crisis priority
      if (analysis.crisisLevel.level === 'high' && method.id === 'crisis-intervention') {
        score += 10;
      }
      
      return { ...method, relevanceScore: score };
    }).sort((a, b) => b.relevanceScore - a.relevanceScore);
  }

  getFastFallbackMethods(userMessage) {
    const message = userMessage.toLowerCase();
    
    // Quick keyword detection for fallback
    if (message.includes('anxious') || message.includes('worry')) {
      return [{
        id: 'cbt-anxiety',
        methodName: 'CBT for Anxiety',
        description: 'Help with anxious thoughts and feelings',
        relevanceScore: 5
      }];
    }
    
    if (message.includes('sad') || message.includes('depressed')) {
      return [{
        id: 'behavioral-activation',
        methodName: 'Behavioral Activation',
        description: 'Gentle activities to improve mood',
        relevanceScore: 5
      }];
    }
    
    // Default fallback
    return [{
      id: 'humanistic-therapy',
      methodName: 'Supportive Listening',
      description: 'Empathetic support and validation',
      relevanceScore: 3
    }];
  }

  async analyzeConversationHistory(userId) {
    if (!userId) {
      return { 
        summary: { sessions: 0, themes: [], emotions: [], progress: 'new_user' },
        patterns: [],
        userProfile: null,
        relationshipContext: null
      };
    }

    // Check cache first
    const cacheKey = userId;
    const cached = this.conversationCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < this.cacheExpiry)) {
      console.log('📋 Using cached conversation analysis');
      return cached.data;
    }

    try {
      // Get comprehensive conversation history
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50); // Increased limit for better analysis

      if (error) {
        console.error('Error fetching conversation history:', error);
        return { summary: { sessions: 0, themes: [], emotions: [] }, patterns: [] };
      }

      if (!conversations || conversations.length === 0) {
        return { 
          summary: { sessions: 0, themes: [], emotions: [], progress: 'new_user' },
          patterns: [],
          userProfile: null,
          relationshipContext: null
        };
      }

      // Enhanced analysis with user profiling
      const userProfile = this.buildUserProfile(conversations);
      const relationshipContext = this.analyzeRelationshipContext(conversations);
      const emotionalJourney = this.trackEmotionalJourney(conversations);
      const personalityInsights = this.extractPersonalityInsights(conversations);
      const communicationPatterns = this.analyzeCommunicationPatterns(conversations);
      const progressTracking = this.trackDetailedProgress(conversations);

      // Traditional analysis
      const patterns = this.extractConversationPatterns(conversations);
      const themes = this.identifyPersonalizedThemes(conversations);
      const emotions = this.trackRecentEmotions(conversations);
      const progress = this.assessPersonalizedProgress(conversations);

      const result = {
        summary: {
          sessions: Math.ceil(conversations.length / 2), // Approximate sessions
          themes,
          emotions,
          progress,
          totalMessages: conversations.length
        },
        patterns,
        userProfile,
        relationshipContext,
        emotionalJourney,
        personalityInsights,
        communicationPatterns,
        progressTracking,
        conversations: conversations.slice(0, 10) // Keep recent 10 for context
      };

      // Cache the result
      this.conversationCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      return result;

    } catch (error) {
      console.error('Error analyzing conversation history:', error);
      return { summary: { sessions: 0, themes: [], emotions: [] }, patterns: [] };
    }
  }

  // Build comprehensive user profile from conversations
  buildUserProfile(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    if (userMessages.length === 0) return null;

    // Simple, robust profile with only methods that definitely exist
    const profile = {
      // Core personality traits - these methods exist for sure
      communicationStyle: this.identifyCommunicationStyle(userMessages),
      emotionalExpression: this.analyzeEmotionalExpression(userMessages),
      responseToSupport: this.analyzeResponseToSupport(conversations),
      
      // Personal details - this method exists
      personalDetails: this.extractPersonalDetails(userMessages),
      regularActivities: this.identifyRegularActivities(userMessages),
      
      // Basic analytics that don't require complex methods
      messageCount: userMessages.length,
      avgMessageLength: userMessages.reduce((sum, m) => sum + (m.message_text?.length || 0), 0) / Math.max(userMessages.length, 1),
      recentMessages: userMessages.slice(-3).map(m => m.message_text),
      
      // Simple keyword-based primary concerns
      primaryConcerns: this.extractSimpleConcerns(userMessages),
      
      // Timestamp info
      profileGeneratedAt: new Date().toISOString(),
      conversationSpan: conversations.length > 1 ? 
        new Date(conversations[conversations.length - 1].created_at) - new Date(conversations[0].created_at) : 0
    };

    return profile;
  }

  // Build relationship memory for continuity
  buildRelationshipMemory(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const therapistMessages = conversations.filter(c => c.sender === 'therapist');
    
    return {
      sharedExperiences: this.extractSharedExperiences(conversations),
      personalReferencePoints: this.extractPersonalReferencePoints(userMessages),
      therapeuticMoments: this.identifyTherapeuticMoments(conversations),
      progressMilestones: this.identifyProgressMilestones(conversations),
      emotionalBreakthroughs: this.identifyEmotionalBreakthroughs(conversations),
      challengesSurcome: this.identifyChallengesSurcome(conversations),
      preferredLanguage: this.identifyPreferredLanguage(therapistMessages),
      relationshipDevelopment: this.trackRelationshipDevelopment(conversations),
      trustBuilding: this.assessTrustBuilding(conversations)
    };
  }

  // Extract personal preferences for better responses
  extractPersonalPreferences(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    
    return {
      communicationPreferences: this.identifyCommuncationPreferences(allText),
      topicPreferences: this.identifyTopicPreferences(allText),
      activityPreferences: this.identifyActivityPreferences(allText),
      timePreferences: this.identifyTimePreferences(userMessages),
      supportStyle: this.identifySupportStyle(allText),
      feedbackPreferences: this.identifyFeedbackPreferences(allText)
    };
  }

  // Analyze life context for more personalized responses
  analyzeLifeContext(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    
    return {
      lifeStage: this.identifyLifeStage(allText),
      majorLifeEvents: this.identifyMajorLifeEvents(allText),
      currentStressors: this.identifyCurrentStressors(allText),
      supportSystem: this.analyzeSupporSystem(allText),
      culturalContext: this.identifyCulturalContext(allText),
      workLifeBalance: this.assessWorkLifeBalance(allText),
      livingSituation: this.identifyLivingSituation(allText),
      healthContext: this.identifyHealthContext(allText)
    };
  }

  // Assess therapeutic needs for better intervention
  assessTherapeuticNeeds(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const recentMessages = userMessages.slice(0, 10);
    
    return {
      urgencyLevel: this.assessUrgencyLevel(recentMessages),
      interventionType: this.identifyBestIntervention(recentMessages),
      readinessForChange: this.assessReadinessForChange(recentMessages),
      barriersToDrogress: this.identifyBarriersToProgress(recentMessages),
      motivationalFactors: this.identifyMotivationalFactors(recentMessages),
      therapeuticGoals: this.identifyTherapeuticGoals(recentMessages)
    };
  }

  // Assess relationship depth for appropriate responses
  assessRelationshipDepth(conversations) {
    const totalMessages = conversations.length;
    const userMessages = conversations.filter(c => c.sender === 'user');
    
    if (totalMessages < 10) return 'initial';
    if (totalMessages < 30) return 'developing';
    if (totalMessages < 60) return 'established';
    if (totalMessages < 100) return 'deep';
    return 'profound';
  }

  // Extract personal details mentioned in conversations
  extractPersonalDetails(userMessages) {
    const allText = userMessages.map(m => m.message_text || '').join(' ');
    
    return {
      names: this.extractNames(allText),
      places: this.extractPlaces(allText),
      interests: this.extractInterests(allText),
      occupations: this.extractOccupations(allText),
      relationships: this.extractRelationshipMentions(allText),
      timeframes: this.extractTimeframes(allText)
    };
  }

  // Identify significant events mentioned
  identifySignificantEvents(userMessages) {
    const events = [];
    const eventKeywords = [
      'birthday', 'anniversary', 'wedding', 'graduation', 'promotion', 'funeral',
      'birth', 'death', 'divorce', 'breakup', 'moved', 'started', 'ended',
      'accident', 'illness', 'surgery', 'hospitalized', 'diagnosed'
    ];

    userMessages.forEach(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      eventKeywords.forEach(keyword => {
        if (text.includes(keyword)) {
          events.push({
            event: keyword,
            context: text.substring(Math.max(0, text.indexOf(keyword) - 50), text.indexOf(keyword) + 50),
            timestamp: msg.created_at
          });
        }
      });
    });

    return events.slice(0, 10); // Keep most recent significant events
  }

  // Identify regular activities mentioned
  identifyRegularActivities(userMessages) {
    const activities = {
      work: ['work', 'job', 'office', 'meeting', 'project', 'boss', 'colleague'],
      exercise: ['gym', 'workout', 'running', 'yoga', 'swimming', 'exercise'],
      social: ['friends', 'family', 'dinner', 'party', 'visit', 'hang out'],
      hobbies: ['reading', 'music', 'art', 'cooking', 'gardening', 'gaming'],
      selfcare: ['meditation', 'therapy', 'massage', 'spa', 'relaxation']
    };

    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    const activityScores = {};

    Object.entries(activities).forEach(([activity, keywords]) => {
      activityScores[activity] = keywords.filter(keyword => allText.includes(keyword)).length;
    });

    return Object.entries(activityScores)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .filter(([,score]) => score > 0)
      .map(([activity]) => activity);
  }

  // Identify communication style patterns
  identifyCommunicationStyle(userMessages) {
    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    
    const communicationPatterns = {
      direct: ['i want', 'i need', 'i think', 'clearly', 'obviously', 'definitely'],
      indirect: ['maybe', 'perhaps', 'i guess', 'sort of', 'kind of', 'i suppose'],
      emotional: ['feel', 'feelings', 'emotions', 'heart', 'gut feeling', 'emotional'],
      analytical: ['analyze', 'think', 'logic', 'reason', 'consider', 'evaluate'],
      storytelling: ['happened', 'story', 'experience', 'remember', 'time when', 'once'],
      questioning: ['why', 'how', 'what if', 'wondering', 'curious', 'question']
    };

    const styleScores = {};
    Object.entries(communicationPatterns).forEach(([style, keywords]) => {
      styleScores[style] = keywords.filter(keyword => allText.includes(keyword)).length;
    });

    // Add sentence structure analysis
    const sentences = allText.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const avgSentenceLength = sentences.reduce((sum, s) => sum + s.trim().split(/\s+/).length, 0) / Math.max(sentences.length, 1);
    
    if (avgSentenceLength > 15) {
      styleScores.detailed = (styleScores.detailed || 0) + 2;
    } else if (avgSentenceLength < 8) {
      styleScores.concise = (styleScores.concise || 0) + 2;
    }

    return Object.entries(styleScores)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2)
      .map(([style]) => style);
  }

  // Analyze emotional expression patterns
  analyzeEmotionalExpression(userMessages) {
    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    
    const expressionStyles = {
      direct: ['feel', 'emotion', 'angry', 'sad', 'happy', 'frustrated'],
      indirect: ['fine', 'okay', 'whatever', 'i guess', 'sort of'],
      metaphorical: ['like', 'as if', 'seems like', 'feels like', 'it\'s like'],
      somatic: ['tired', 'tense', 'heavy', 'light', 'tight', 'empty']
    };

    const styleScores = {};
    Object.entries(expressionStyles).forEach(([style, keywords]) => {
      styleScores[style] = keywords.filter(keyword => allText.includes(keyword)).length;
    });

    return Object.entries(styleScores)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2)
      .map(([style]) => style);
  }

  // Analyze response to support
  analyzeResponseToSupport(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const therapistMessages = conversations.filter(c => c.sender === 'therapist');
    
    let positiveResponses = 0;
    let resistantResponses = 0;
    let neutralResponses = 0;

    // Look for patterns in user responses after therapist messages
    for (let i = 0; i < conversations.length - 1; i++) {
      if (conversations[i].sender === 'therapist' && conversations[i + 1].sender === 'user') {
        const userResponse = conversations[i + 1].message_text?.toLowerCase() || '';
        
        if (userResponse.includes('thank') || userResponse.includes('helpful') || 
            userResponse.includes('better') || userResponse.includes('yes')) {
          positiveResponses++;
        } else if (userResponse.includes('but') || userResponse.includes('however') || 
                   userResponse.includes('no') || userResponse.includes('not really')) {
          resistantResponses++;
        } else {
          neutralResponses++;
        }
      }
    }

    const total = positiveResponses + resistantResponses + neutralResponses;
    if (total === 0) return 'unknown';

    if (positiveResponses / total > 0.6) return 'receptive';
    if (resistantResponses / total > 0.4) return 'resistant';
    return 'mixed';
  }

  // Extract shared experiences for relationship building
  extractSharedExperiences(conversations) {
    const experiences = [];
    const userMessages = conversations.filter(c => c.sender === 'user');
    
    // Look for moments where user shared something personal
    const personalShares = userMessages.filter(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      return text.includes('i remember') || text.includes('when i was') ||
             text.includes('my') || text.includes('happened to me') ||
             text.includes('i experienced') || text.includes('i went through');
    });

    personalShares.forEach(share => {
      experiences.push({
        content: share.message_text,
        timestamp: share.created_at,
        type: 'personal_share'
      });
    });

    return experiences.slice(0, 20); // Keep most meaningful shared experiences
  }

  // Extract personal reference points
  extractPersonalReferencePoints(userMessages) {
    const references = {
      people: [],
      places: [],
      events: [],
      objects: [],
      memories: []
    };

    const allText = userMessages.map(m => m.message_text || '').join(' ');
    
    // Use regex to find personal references
    const namePattern = /\b[A-Z][a-z]+\b/g;
    const placePattern = /\b(home|work|school|hospital|city|town|country)\b/gi;
    const eventPattern = /\b(birthday|wedding|funeral|graduation|anniversary)\b/gi;
    
    const names = allText.match(namePattern) || [];
    const places = allText.match(placePattern) || [];
    const events = allText.match(eventPattern) || [];
    
    references.people = [...new Set(names)].slice(0, 10);
    references.places = [...new Set(places)].slice(0, 10);
    references.events = [...new Set(events)].slice(0, 10);

    return references;
  }

  // Identify therapeutic moments
  identifyTherapeuticMoments(conversations) {
    const moments = [];
    
    conversations.forEach((msg, index) => {
      if (msg.sender === 'user') {
        const text = msg.message_text?.toLowerCase() || '';
        
        // Identify breakthrough moments
        if (text.includes('i realize') || text.includes('i understand') ||
            text.includes('makes sense') || text.includes('i see') ||
            text.includes('never thought') || text.includes('aha')) {
          moments.push({
            type: 'breakthrough',
            content: msg.message_text,
            timestamp: msg.created_at
          });
        }
        
        // Identify emotional moments
        if (text.includes('crying') || text.includes('tears') ||
            text.includes('emotional') || text.includes('overwhelmed')) {
          moments.push({
            type: 'emotional',
            content: msg.message_text,
            timestamp: msg.created_at
          });
        }
        
        // Identify gratitude moments
        if (text.includes('thank') || text.includes('grateful') ||
            text.includes('appreciate') || text.includes('helped')) {
          moments.push({
            type: 'gratitude',
            content: msg.message_text,
            timestamp: msg.created_at
          });
        }
      }
    });

    return moments.slice(0, 15); // Keep most significant therapeutic moments
  }

  // Helper methods for detailed analysis
  extractNames(text) {
    const namePattern = /\b[A-Z][a-z]+\b/g;
    const potentialNames = text.match(namePattern) || [];
    return [...new Set(potentialNames)].slice(0, 10);
  }

  extractPlaces(text) {
    const placeKeywords = ['home', 'work', 'school', 'hospital', 'city', 'town', 'country', 'office', 'gym'];
    return placeKeywords.filter(place => text.toLowerCase().includes(place));
  }

  extractInterests(text) {
    const interestKeywords = ['music', 'art', 'sports', 'reading', 'cooking', 'travel', 'movies', 'games'];
    return interestKeywords.filter(interest => text.toLowerCase().includes(interest));
  }

  extractOccupations(text) {
    const jobKeywords = ['teacher', 'nurse', 'doctor', 'engineer', 'manager', 'student', 'artist', 'writer'];
    return jobKeywords.filter(job => text.toLowerCase().includes(job));
  }

  extractRelationshipMentions(text) {
    const relationshipTerms = {
      romantic: ['partner', 'boyfriend', 'girlfriend', 'husband', 'wife', 'spouse', 'fiance', 'dating', 'relationship'],
      family: ['mother', 'father', 'mom', 'dad', 'parent', 'sibling', 'brother', 'sister', 'child', 'son', 'daughter', 'family'],
      social: ['friend', 'coworker', 'colleague', 'neighbor', 'roommate', 'classmate'],
      professional: ['boss', 'manager', 'supervisor', 'employee', 'client', 'customer']
    };

    const mentions = {};
    Object.entries(relationshipTerms).forEach(([category, terms]) => {
      const found = terms.filter(term => text.toLowerCase().includes(term.toLowerCase()));
      if (found.length > 0) {
        mentions[category] = found;
      }
    });

    return mentions;
  }

  extractTimeframes(text) {
    const timeIndicators = {
      recent: ['today', 'yesterday', 'this week', 'recently', 'lately', 'just now', 'right now'],
      past: ['last month', 'last year', 'ago', 'before', 'previously', 'used to', 'back then'],
      ongoing: ['always', 'still', 'continuing', 'ongoing', 'keeps happening', 'every day'],
      future: ['tomorrow', 'next week', 'next month', 'planning', 'will', 'going to', 'hope to']
    };

    const timeframes = {};
    Object.entries(timeIndicators).forEach(([timeframe, indicators]) => {
      const found = indicators.filter(indicator => text.toLowerCase().includes(indicator.toLowerCase()));
      if (found.length > 0) {
        timeframes[timeframe] = found;
      }
    });

    return timeframes;
  }

  identifyCommuncationPreferences(text) {
    if (text.includes('direct') || text.includes('straight')) return 'direct';
    if (text.includes('gentle') || text.includes('soft')) return 'gentle';
    if (text.includes('detailed') || text.includes('explain')) return 'detailed';
    return 'balanced';
  }

  identifySupportStyle(text) {
    if (text.includes('advice') || text.includes('tell me what')) return 'advice_seeking';
    if (text.includes('listen') || text.includes('understand')) return 'validation_seeking';
    if (text.includes('solution') || text.includes('fix')) return 'solution_focused';
    return 'mixed';
  }

  identifyLifeStage(text) {
    if (text.includes('student') || text.includes('college')) return 'student';
    if (text.includes('career') || text.includes('job')) return 'career_focused';
    if (text.includes('parent') || text.includes('children')) return 'parent';
    if (text.includes('retired') || text.includes('retirement')) return 'retired';
    return 'adult';
  }

  assessEngagementLevel(userMessages) {
    const avgLength = userMessages.reduce((sum, msg) => sum + (msg.message_text?.length || 0), 0) / userMessages.length;
    const questionCount = userMessages.filter(msg => msg.message_text?.includes('?')).length;
    const detailLevel = userMessages.filter(msg => (msg.message_text?.length || 0) > 100).length;
    
    if (avgLength > 150 && questionCount > 2 && detailLevel > 3) return 'high';
    if (avgLength > 80 && questionCount > 1) return 'medium';
    return 'low';
  }

  assessOpennessToDifficultTopics(userMessages) {
    const difficultTopics = ['trauma', 'abuse', 'depression', 'anxiety', 'suicide', 'addiction', 'grief'];
    const mentionCount = userMessages.filter(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      return difficultTopics.some(topic => text.includes(topic));
    }).length;
    
    if (mentionCount > 5) return 'very_open';
    if (mentionCount > 2) return 'moderately_open';
    if (mentionCount > 0) return 'cautiously_open';
    return 'reserved';
  }

  assessHumorAppreciation(userMessages) {
    const humorIndicators = ['lol', 'haha', 'funny', 'joke', 'laugh', '😂', '😄', '🙂'];
    const humorCount = userMessages.filter(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      return humorIndicators.some(indicator => text.includes(indicator));
    }).length;
    
    return humorCount > 3 ? 'high' : humorCount > 1 ? 'medium' : 'low';
  }

  // Missing methods for enhanced method details
  getTherapeuticTiming(methodId, readiness) {
    // Simple timing assessment based on readiness
    const timingMap = {
      'high_readiness': 'immediate',
      'moderate_readiness': 'gradual',
      'resistance': 'gentle_introduction',
      'exploration': 'flexible'
    };
    return timingMap[readiness] || 'standard';
  }

  getEmotionalDepth(methodId, hiddenEmotions) {
    // Assess emotional depth required based on hidden emotions
    if (hiddenEmotions.length === 0) return 'surface';
    if (hiddenEmotions.some(e => ['trauma_responses', 'attachment_wounds'].includes(e.emotion))) {
      return 'deep';
    }
    return 'moderate';
  }

  getConversationFit(methodId, conversationPatterns) {
    // Simple conversation fit assessment
    const sessionCount = conversationPatterns?.summary?.sessions || 0;
    if (sessionCount === 0) return 'introductory';
    if (sessionCount < 5) return 'building';
    return 'established';
  }

  // Method to update conversation memory after each interaction
  async updateConversationMemory(userId, userMessage, therapistResponse, therapeuticMethods) {
    if (!userId) return;
    
    try {
      // Store the conversation in Supabase with enhanced metadata
      const { error } = await supabase
        .from('conversations')
        .insert({
          user_id: userId,
          message_text: userMessage,
          sender: 'user',
          tokens_used: 2,
          created_at: new Date().toISOString()
        });
      
      if (error) {
        console.error('Error storing user message:', error);
      }
      
      // Store therapist response
      const { error: responseError } = await supabase
        .from('conversations')
        .insert({
          user_id: userId,
          message_text: therapistResponse,
          sender: 'therapist',
          tokens_used: 0,
          created_at: new Date().toISOString()
        });
      
      if (responseError) {
        console.error('Error storing therapist response:', responseError);
      }
      
      // Clear cache to force refresh on next analysis
      this.conversationCache.delete(userId);
      
    } catch (error) {
      console.error('Error updating conversation memory:', error);
    }
  }

  // Simple concerns extraction using keyword matching
  extractSimpleConcerns(userMessages) {
    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    
    const concernCategories = {
      anxiety: ['anxious', 'anxiety', 'worry', 'worried', 'nervous', 'panic', 'stress', 'stressed'],
      depression: ['depressed', 'depression', 'sad', 'sadness', 'hopeless', 'empty', 'down'],
      relationships: ['relationship', 'partner', 'marriage', 'divorce', 'breakup', 'lonely', 'friends'],
      work: ['work', 'job', 'career', 'boss', 'colleague', 'workplace', 'unemployment'],
      family: ['family', 'parent', 'mother', 'father', 'child', 'sibling', 'family issues'],
      health: ['health', 'sick', 'illness', 'pain', 'medical', 'doctor', 'hospital'],
      sleep: ['sleep', 'insomnia', 'tired', 'exhausted', 'sleepless', 'nightmares'],
      selfesteem: ['worthless', 'useless', 'self-esteem', 'confidence', 'insecure', 'inadequate']
    };

    const concernScores = {};
    Object.entries(concernCategories).forEach(([concern, keywords]) => {
      concernScores[concern] = keywords.filter(keyword => allText.includes(keyword)).length;
    });

    return Object.entries(concernScores)
      .filter(([, score]) => score > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([concern]) => concern);
  }

  // Detect surface-level symptoms from user message
  detectSurfaceSymptoms(userMessage) {
    const text = userMessage.toLowerCase();
    const symptoms = [];
    
    // Check for explicit symptom mentions
    const symptomPatterns = {
      anxiety: ['anxious', 'anxiety', 'worry', 'nervous', 'panic', 'stress'],
      depression: ['depressed', 'sad', 'hopeless', 'empty', 'down', 'blue'],
      anger: ['angry', 'mad', 'furious', 'rage', 'irritated', 'frustrated'],
      sleep: ['insomnia', 'sleepless', 'tired', 'exhausted', 'can\'t sleep'],
      relationships: ['relationship issues', 'fighting', 'arguing', 'breakup', 'divorce'],
      work: ['work stress', 'job pressure', 'workplace', 'boss problems'],
      trauma: ['trauma', 'ptsd', 'flashbacks', 'nightmares', 'triggered'],
      eating: ['eating disorder', 'anorexia', 'bulimia', 'binge eating'],
      addiction: ['addiction', 'alcoholism', 'substance abuse', 'dependency']
    };

    Object.entries(symptomPatterns).forEach(([symptom, keywords]) => {
      if (keywords.some(keyword => text.includes(keyword))) {
        symptoms.push(symptom);
      }
    });

    return symptoms;
  }

  // Detect hidden emotional patterns beneath surface presentation
  detectHiddenEmotions(userMessage) {
    const text = userMessage.toLowerCase();
    const hiddenEmotions = [];

    Object.entries(this.emotionalPatterns).forEach(([emotion, patterns]) => {
      const matches = patterns.filter(pattern => text.includes(pattern.toLowerCase()));
      if (matches.length > 0) {
        hiddenEmotions.push({
          emotion,
          intensity: matches.length > 2 ? 'high' : matches.length > 1 ? 'medium' : 'low',
          indicators: matches.slice(0, 3) // Limit for performance
        });
      }
    });

    return hiddenEmotions.sort((a, b) => {
      const intensityOrder = { high: 3, medium: 2, low: 1 };
      return intensityOrder[b.intensity] - intensityOrder[a.intensity];
    });
  }

  // Assess user's readiness for therapeutic intervention
  assessTherapeuticReadiness(userMessage) {
    const text = userMessage.toLowerCase();
    
    const readinessIndicators = this.therapeuticTiming.readiness_for_change || [];
    const resistanceIndicators = this.therapeuticTiming.resistance_patterns || [];
    const breakthroughIndicators = this.therapeuticTiming.breakthrough_indicators || [];

    const readinessScore = readinessIndicators.filter(indicator => 
      text.includes(indicator.toLowerCase())
    ).length;
    
    const resistanceScore = resistanceIndicators.filter(indicator => 
      text.includes(indicator.toLowerCase())
    ).length;
    
    const breakthroughScore = breakthroughIndicators.filter(indicator => 
      text.includes(indicator.toLowerCase())
    ).length;

    if (breakthroughScore > 0) return 'breakthrough_moment';
    if (readinessScore > resistanceScore && readinessScore > 1) return 'high_readiness';
    if (resistanceScore > readinessScore) return 'resistance';
    if (readinessScore > 0) return 'moderate_readiness';
    
    return 'exploring';
  }

  // Assess crisis level and urgency
  assessCrisisLevel(userMessage) {
    const text = userMessage.toLowerCase();
    const crisisIndicators = this.therapeuticTiming.crisis_indicators || [];
    
    const crisisMatches = crisisIndicators.filter(indicator => 
      text.includes(indicator.toLowerCase())
    );

    const level = crisisMatches.length >= 2 ? 'high' : 
                  crisisMatches.length === 1 ? 'moderate' : 'low';
    
    return {
      level,
      indicators: crisisMatches,
      requiresImmediate: level === 'high',
      interventionNeeded: level !== 'low'
    };
  }

  // Analyze relationship context and dynamics from conversation history
  analyzeRelationshipContext(conversations) {
    const relationshipMentions = [];
    const conflictPatterns = [];
    const attachmentIndicators = [];
    const gottmanRedFlags = {
      criticism: [],
      contempt: [],
      defensiveness: [],
      stonewalling: []
    };

    conversations.forEach(conv => {
      const text = conv.message_text?.toLowerCase() || '';
      
      // Detect relationship mentions
      const relationships = ['partner', 'boyfriend', 'girlfriend', 'husband', 'wife', 'spouse'];
      relationships.forEach(rel => {
        if (text.includes(rel)) {
          relationshipMentions.push({ type: rel, context: text.slice(0, 100) });
        }
      });

      // Detect Four Horsemen patterns
      if (text.includes('always') || text.includes('never') || text.includes('you are')) {
        gottmanRedFlags.criticism.push(text.slice(0, 100));
      }
      if (text.includes('stupid') || text.includes('pathetic') || text.includes('rolling eyes')) {
        gottmanRedFlags.contempt.push(text.slice(0, 100));
      }
      if (text.includes('but') || text.includes('not my fault') || text.includes('actually')) {
        gottmanRedFlags.defensiveness.push(text.slice(0, 100));
      }
      if (text.includes('shut down') || text.includes('silent treatment') || text.includes('withdraw')) {
        gottmanRedFlags.stonewalling.push(text.slice(0, 100));
      }

      // Detect conflict patterns
      const conflictKeywords = ['fight', 'argue', 'conflict', 'disagreement', 'tension'];
      conflictKeywords.forEach(keyword => {
        if (text.includes(keyword)) {
          conflictPatterns.push({ keyword, context: text.slice(0, 100) });
        }
      });

      // Detect attachment patterns
      if (text.includes('abandon') || text.includes('leave me')) {
        attachmentIndicators.push({ type: 'anxious', context: text.slice(0, 100) });
      }
      if (text.includes('need space') || text.includes('too clingy')) {
        attachmentIndicators.push({ type: 'avoidant', context: text.slice(0, 100) });
      }
    });

    return {
      relationshipMentions,
      conflictPatterns,
      attachmentIndicators,
      gottmanRedFlags,
      hasRelationshipContent: relationshipMentions.length > 0,
      riskLevel: gottmanRedFlags.criticism.length + gottmanRedFlags.contempt.length > 3 ? 'high' : 'low'
    };
  }

  // Track emotional journey and progression over time
  trackEmotionalJourney(conversations) {
    const emotionalTimeline = [];
    const emotionTrends = {};
    const breakthroughMoments = [];

    conversations.forEach((conv, index) => {
      const text = conv.message_text?.toLowerCase() || '';
      const timestamp = conv.created_at;
      
      // Detect emotional states
      const emotions = {
        anxiety: ['anxious', 'worried', 'nervous', 'panic'],
        depression: ['sad', 'hopeless', 'empty', 'depressed'],
        anger: ['angry', 'mad', 'frustrated', 'furious'],
        joy: ['happy', 'excited', 'joyful', 'content'],
        hope: ['hopeful', 'optimistic', 'better', 'improving']
      };

      const detectedEmotions = {};
      Object.entries(emotions).forEach(([emotion, keywords]) => {
        const matches = keywords.filter(keyword => text.includes(keyword));
        if (matches.length > 0) {
          detectedEmotions[emotion] = matches.length;
        }
      });

      if (Object.keys(detectedEmotions).length > 0) {
        emotionalTimeline.push({
          timestamp,
          emotions: detectedEmotions,
          messageIndex: index,
          context: text.slice(0, 150)
        });
      }

      // Track breakthrough moments
      const breakthroughKeywords = ['realize', 'understand', 'clarity', 'aha', 'breakthrough'];
      breakthroughKeywords.forEach(keyword => {
        if (text.includes(keyword)) {
          breakthroughMoments.push({
            timestamp,
            type: keyword,
            context: text.slice(0, 150)
          });
        }
      });

      // Update emotion trends
      Object.keys(detectedEmotions).forEach(emotion => {
        if (!emotionTrends[emotion]) emotionTrends[emotion] = [];
        emotionTrends[emotion].push({ timestamp, intensity: detectedEmotions[emotion] });
      });
    });

    return {
      emotionalTimeline,
      emotionTrends,
      breakthroughMoments,
      overallProgression: this.calculateEmotionalProgression(emotionTrends),
      mostFrequentEmotion: this.getMostFrequentEmotion(emotionTrends)
    };
  }

  // Extract personality insights from communication patterns
  extractPersonalityInsights(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const communicationStyle = {};
    const cognitivePatterns = {};
    const copingMechanisms = [];

    // Analyze communication style
    const allText = userMessages.map(m => m.message_text?.toLowerCase() || '').join(' ');
    
    // Introversion vs Extraversion
    const introvertedWords = ['alone', 'quiet', 'solitude', 'recharge', 'overwhelmed by people'];
    const extravertedWords = ['social', 'party', 'people', 'energized', 'outgoing'];
    
    communicationStyle.introversion = introvertedWords.filter(word => allText.includes(word)).length;
    communicationStyle.extraversion = extravertedWords.filter(word => allText.includes(word)).length;

    // Thinking vs Feeling
    const thinkingWords = ['logical', 'analyze', 'rational', 'objective', 'facts'];
    const feelingWords = ['feel', 'emotions', 'values', 'heart', 'empathy'];
    
    communicationStyle.thinking = thinkingWords.filter(word => allText.includes(word)).length;
    communicationStyle.feeling = feelingWords.filter(word => allText.includes(word)).length;

    // Detect cognitive patterns
    if (allText.includes('should') || allText.includes('must') || allText.includes('have to')) {
      cognitivePatterns.perfectionistic = true;
    }
    if (allText.includes('what if') || allText.includes('worry') || allText.includes('anxious')) {
      cognitivePatterns.catastrophizing = true;
    }
    if (allText.includes('always') || allText.includes('never') || allText.includes('everyone')) {
      cognitivePatterns.allOrNothing = true;
    }

    // Identify coping mechanisms
    const copingKeywords = {
      avoidance: ['avoid', 'ignore', 'distract', 'escape'],
      seeking_support: ['talk', 'help', 'support', 'reach out'],
      problem_solving: ['plan', 'solution', 'fix', 'strategy'],
      emotional_regulation: ['breathe', 'meditate', 'calm', 'relax']
    };

    Object.entries(copingKeywords).forEach(([mechanism, keywords]) => {
      const matches = keywords.filter(keyword => allText.includes(keyword));
      if (matches.length > 0) {
        copingMechanisms.push({ mechanism, frequency: matches.length });
      }
    });

    return {
      communicationStyle,
      cognitivePatterns,
      copingMechanisms,
      personalityType: this.inferPersonalityType(communicationStyle),
      strengths: this.identifyPersonalityStrengths(communicationStyle, copingMechanisms)
    };
  }

  // Analyze communication patterns and preferences
  analyzeCommunicationPatterns(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const patterns = {
      messageLength: [],
      responseTime: [],
      emotionalExpression: {},
      questionAsking: 0,
      storytelling: 0,
      directness: 0
    };

    userMessages.forEach((message, index) => {
      const text = message.message_text || '';
      patterns.messageLength.push(text.length);

      // Analyze emotional expression
      if (text.includes('!')) patterns.emotionalExpression.exclamatory = (patterns.emotionalExpression.exclamatory || 0) + 1;
      if (text.includes('...')) patterns.emotionalExpression.hesitant = (patterns.emotionalExpression.hesitant || 0) + 1;
      if (text.match(/[A-Z]{2,}/)) patterns.emotionalExpression.emphatic = (patterns.emotionalExpression.emphatic || 0) + 1;

      // Count questions
      if (text.includes('?')) patterns.questionAsking++;

      // Detect storytelling
      if (text.length > 100 && (text.includes('yesterday') || text.includes('last week') || text.includes('happened'))) {
        patterns.storytelling++;
      }

      // Measure directness
      if (text.length < 50 && !text.includes('maybe') && !text.includes('kind of')) {
        patterns.directness++;
      }
    });

    return {
      avgMessageLength: patterns.messageLength.reduce((a, b) => a + b, 0) / patterns.messageLength.length,
      communicationStyle: this.inferCommunicationStyle(patterns),
      emotionalExpression: patterns.emotionalExpression,
      engagementLevel: patterns.questionAsking + patterns.storytelling,
      directnessScore: patterns.directness / userMessages.length
    };
  }

  // Track detailed progress across multiple dimensions
  trackDetailedProgress(conversations) {
    const progressMetrics = {
      emotionalRegulation: this.trackEmotionalRegulationProgress(conversations),
      selfAwareness: this.trackSelfAwarenessProgress(conversations),
      copingSkills: this.trackCopingSkillsProgress(conversations),
      relationshipSkills: this.trackRelationshipSkillsProgress(conversations)
    };

    const overallProgress = this.calculateOverallProgress(progressMetrics);
    
    return {
      ...progressMetrics,
      overallProgress,
      progressSummary: this.generateProgressSummary(progressMetrics),
      nextSteps: this.suggestNextSteps(progressMetrics)
    };
  }

  // Helper methods for complex analysis
  calculateEmotionalProgression(emotionTrends) {
    // Simple implementation - can be enhanced
    const positiveEmotions = ['joy', 'hope', 'content'];
    const negativeEmotions = ['anxiety', 'depression', 'anger'];
    
    let positiveCount = 0;
    let negativeCount = 0;
    
    Object.keys(emotionTrends).forEach(emotion => {
      if (positiveEmotions.includes(emotion)) positiveCount += emotionTrends[emotion].length;
      if (negativeEmotions.includes(emotion)) negativeCount += emotionTrends[emotion].length;
    });
    
    return positiveCount > negativeCount ? 'improving' : negativeCount > positiveCount ? 'declining' : 'stable';
  }

  getMostFrequentEmotion(emotionTrends) {
    let maxCount = 0;
    let mostFrequent = 'neutral';
    
    Object.entries(emotionTrends).forEach(([emotion, instances]) => {
      if (instances.length > maxCount) {
        maxCount = instances.length;
        mostFrequent = emotion;
      }
    });
    
    return mostFrequent;
  }

  inferPersonalityType(communicationStyle) {
    // Simple MBTI-like inference
    const isIntroverted = communicationStyle.introversion > communicationStyle.extraversion;
    const isFeeling = communicationStyle.feeling > communicationStyle.thinking;
    
    if (isIntroverted && isFeeling) return 'Introspective Feeler';
    if (isIntroverted && !isFeeling) return 'Analytical Thinker';
    if (!isIntroverted && isFeeling) return 'Expressive Feeler';
    return 'Logical Communicator';
  }

  identifyPersonalityStrengths(communicationStyle, copingMechanisms) {
    const strengths = [];
    
    if (communicationStyle.feeling > 2) strengths.push('High Emotional Intelligence');
    if (communicationStyle.thinking > 2) strengths.push('Analytical Problem Solving');
    if (copingMechanisms.some(m => m.mechanism === 'seeking_support')) strengths.push('Willingness to Seek Help');
    if (copingMechanisms.some(m => m.mechanism === 'problem_solving')) strengths.push('Solution-Oriented Mindset');
    
    return strengths;
  }

  inferCommunicationStyle(patterns) {
    if (patterns.directness > 0.7) return 'Direct';
    if (patterns.storytelling > patterns.questionAsking) return 'Narrative';
    if (patterns.questionAsking > patterns.storytelling) return 'Inquisitive';
    return 'Balanced';
  }

  trackEmotionalRegulationProgress(conversations) {
    // Simplified implementation
    const userMessages = conversations.filter(c => c.sender === 'user');
    const regulationKeywords = ['calm', 'breathe', 'manage', 'control', 'regulate'];
    
    let progressScore = 0;
    userMessages.forEach(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      regulationKeywords.forEach(keyword => {
        if (text.includes(keyword)) progressScore++;
      });
    });
    
    return { score: progressScore, level: progressScore > 3 ? 'good' : 'developing' };
  }

  trackSelfAwarenessProgress(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const awarenessKeywords = ['realize', 'understand', 'notice', 'aware', 'recognize'];
    
    let progressScore = 0;
    userMessages.forEach(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      awarenessKeywords.forEach(keyword => {
        if (text.includes(keyword)) progressScore++;
      });
    });
    
    return { score: progressScore, level: progressScore > 3 ? 'high' : 'developing' };
  }

  trackCopingSkillsProgress(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const copingKeywords = ['strategy', 'technique', 'practice', 'exercise', 'skill'];
    
    let progressScore = 0;
    userMessages.forEach(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      copingKeywords.forEach(keyword => {
        if (text.includes(keyword)) progressScore++;
      });
    });
    
    return { score: progressScore, level: progressScore > 2 ? 'good' : 'building' };
  }

  trackRelationshipSkillsProgress(conversations) {
    const userMessages = conversations.filter(c => c.sender === 'user');
    const relationshipKeywords = ['communicate', 'listen', 'understand', 'empathy', 'connect'];
    
    let progressScore = 0;
    userMessages.forEach(msg => {
      const text = msg.message_text?.toLowerCase() || '';
      relationshipKeywords.forEach(keyword => {
        if (text.includes(keyword)) progressScore++;
      });
    });
    
    return { score: progressScore, level: progressScore > 2 ? 'strong' : 'developing' };
  }

  calculateOverallProgress(progressMetrics) {
    const scores = Object.values(progressMetrics).map(metric => metric.score || 0);
    const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    if (averageScore > 3) return 'excellent';
    if (averageScore > 2) return 'good';
    if (averageScore > 1) return 'developing';
    return 'beginning';
  }

  generateProgressSummary(progressMetrics) {
    const areas = Object.keys(progressMetrics);
    const strongAreas = areas.filter(area => progressMetrics[area].score > 2);
    const developingAreas = areas.filter(area => progressMetrics[area].score <= 2);
    
    return {
      strongAreas,
      developingAreas,
      keyInsight: strongAreas.length > developingAreas.length ? 
        'Making good progress across multiple areas' : 
        'Building foundation skills with room for growth'
    };
  }

  suggestNextSteps(progressMetrics) {
    const suggestions = [];
    
    if (progressMetrics.emotionalRegulation?.score < 2) {
      suggestions.push('Focus on emotional regulation techniques');
    }
    if (progressMetrics.selfAwareness?.score < 2) {
      suggestions.push('Develop mindfulness and self-reflection practices');
    }
    if (progressMetrics.copingSkills?.score < 2) {
      suggestions.push('Learn and practice new coping strategies');
    }
    if (progressMetrics.relationshipSkills?.score < 2) {
      suggestions.push('Work on communication and relationship skills');
    }
    
    return suggestions.length > 0 ? suggestions : ['Continue current therapeutic practices'];
  }

  // Missing methods that were being called but not defined
  extractConversationPatterns(conversations) {
    const patterns = [];
    const userMessages = conversations.filter(c => c.sender === 'user');
    
    if (userMessages.length === 0) return patterns;
    
    // Extract recurring themes
    const themes = new Map();
    userMessages.forEach(msg => {
      const words = msg.message_text.toLowerCase().split(/\s+/);
      words.forEach(word => {
        if (word.length > 4) {
          themes.set(word, (themes.get(word) || 0) + 1);
        }
      });
    });
    
    // Convert to patterns array
    Array.from(themes.entries())
      .filter(([word, count]) => count > 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .forEach(([word, count]) => {
        patterns.push({
          type: 'recurring_theme',
          content: word,
          frequency: count,
          significance: count > 5 ? 'high' : 'medium'
        });
      });
    
    return patterns;
  }

  identifyPersonalizedThemes(conversations) {
    const themes = [];
    const userMessages = conversations.filter(c => c.sender === 'user');
    
    if (userMessages.length === 0) return themes;
    
    // Emotional themes
    const emotionalKeywords = {
      anxiety: ['anxious', 'worry', 'nervous', 'panic', 'stress'],
      depression: ['sad', 'depressed', 'hopeless', 'empty', 'down'],
      relationships: ['relationship', 'partner', 'family', 'friend', 'lonely'],
      work: ['work', 'job', 'career', 'boss', 'colleague', 'workplace'],
      self_worth: ['confidence', 'self-esteem', 'worth', 'value', 'doubt']
    };
    
    Object.entries(emotionalKeywords).forEach(([theme, keywords]) => {
      let count = 0;
      userMessages.forEach(msg => {
        const text = msg.message_text.toLowerCase();
        keywords.forEach(keyword => {
          if (text.includes(keyword)) count++;
        });
      });
      
      if (count > 0) {
        themes.push({
          theme,
          frequency: count,
          intensity: count > 5 ? 'high' : count > 2 ? 'medium' : 'low'
        });
      }
    });
    
    return themes.sort((a, b) => b.frequency - a.frequency);
  }

  trackRecentEmotions(conversations) {
    const emotions = [];
    const recentMessages = conversations
      .filter(c => c.sender === 'user')
      .slice(-10); // Last 10 user messages
    
    const emotionKeywords = {
      happy: ['happy', 'joy', 'excited', 'good', 'great', 'wonderful'],
      sad: ['sad', 'upset', 'disappointed', 'hurt', 'crying'],
      angry: ['angry', 'mad', 'frustrated', 'annoyed', 'irritated'],
      anxious: ['anxious', 'worried', 'nervous', 'scared', 'panic'],
      confused: ['confused', 'lost', 'unsure', 'don\'t know', 'unclear']
    };
    
    Object.entries(emotionKeywords).forEach(([emotion, keywords]) => {
      let count = 0;
      recentMessages.forEach(msg => {
        const text = msg.message_text.toLowerCase();
        keywords.forEach(keyword => {
          if (text.includes(keyword)) count++;
        });
      });
      
      if (count > 0) {
        emotions.push({
          emotion,
          frequency: count,
          recency: 'recent'
        });
      }
    });
    
    return emotions.sort((a, b) => b.frequency - a.frequency);
  }

  assessPersonalizedProgress(conversations) {
    const progress = {
      overall: 'beginning',
      areas: {},
      insights: []
    };
    
    if (conversations.length === 0) return progress;
    
    const sessionCount = Math.ceil(conversations.length / 2);
    const userMessages = conversations.filter(c => c.sender === 'user');
    
    // Assess based on conversation depth and engagement
    if (sessionCount > 10) {
      progress.overall = 'developing';
    }
    if (sessionCount > 20) {
      progress.overall = 'progressing';
    }
    if (sessionCount > 30) {
      progress.overall = 'established';
    }
    
    // Look for progress indicators in recent messages
    const recentUserMessages = userMessages.slice(-5);
    const progressIndicators = [
      'i understand now',
      'that helps',
      'feeling better',
      'making progress',
      'i realize',
      'learned that',
      'thank you'
    ];
    
    let progressCount = 0;
    recentUserMessages.forEach(msg => {
      const text = msg.message_text.toLowerCase();
      progressIndicators.forEach(indicator => {
        if (text.includes(indicator)) progressCount++;
      });
    });
    
    if (progressCount > 0) {
      progress.insights.push('Shows signs of therapeutic progress');
    }
    
    progress.areas = {
      engagement: sessionCount > 5 ? 'good' : 'developing',
      openness: userMessages.length > conversations.length * 0.4 ? 'high' : 'medium',
      reflection: progressCount > 0 ? 'present' : 'emerging'
    };
    
    return progress;
  }
}

export default AdvancedTherapyRAG;
